
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { UsersTable } from './UsersTable';
import { supabase } from '@/integrations/supabase/client';
import { Search, Download, Plus } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { useIsMobile } from '@/hooks/use-mobile';
import { ResponsiveTable } from '@/components/ui/responsive-table';

export function UsersContent() {
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isMobile = useIsMobile();

  const {
    data: usersWithCourses = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['users-with-courses'],
    queryFn: async () => {
      try {
        console.log('Fetching all authenticated users via edge function');
        
        // Call the edge function that runs with service_role privileges
        const { data, error: functionError } = await supabase.functions.invoke('get-admin-users');
        
        if (functionError) {
          console.error('Edge function error:', functionError);
          throw functionError;
        }

        if (!data || !data.users) {
          throw new Error('No user data returned from edge function');
        }

        console.log('All users with complete data:', data.users);
        return data.users;
      } catch (err: any) {
        console.error('Exception in query:', err);
        setErrorMessage(err.message || "Failed to fetch users data");
        throw err;
      }
    },
    retry: 1
  });

  const filteredUsers = usersWithCourses.filter(user => 
    user.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) || 
    user.city?.toLowerCase().includes(searchQuery.toLowerCase()) || 
    user.address_state?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return <div className="space-y-4 md:space-y-6">
      <div className="mobile-action-group">
        <h1 className="text-xl md:text-2xl font-semibold">Users</h1>
        <div className={`flex items-center gap-2 ${isMobile ? 'mt-2' : ''}`}>
          <Button variant="outline" size={isMobile ? "sm" : "default"}>
            <Download className="w-4 h-4 mr-2" />
            {!isMobile && "Export"}
          </Button>
          
          <Button variant="default" className="bg-[#1a1a1a]" size={isMobile ? "sm" : "default"}>
            <Plus className="w-4 h-4 mr-2" />
            {!isMobile ? "Add user" : "Add"}
          </Button>
        </div>
      </div>

      {errorMessage && <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>}

      <div className="flex flex-col sm:flex-row justify-between gap-2 md:gap-4">
        <span className="text-sm">
          {filteredUsers.length} users
          {!isLoading && !error && (
            <span className="text-muted-foreground ml-1">
              (100% of your user base)
            </span>
          )}
        </span>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search users" className="pl-9" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
      </div>

      <ResponsiveTable compact={isMobile}>
        <UsersTable users={filteredUsers} isLoading={isLoading} />
      </ResponsiveTable>
    </div>;
}
